import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListAltIcon from '@mui/icons-material/ListAlt';
import type { MyTaskDto } from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { errorMessage } from '../../../shared/api/client';
import { counts } from './my-tasks';
import { MyTaskList } from './MyTaskList';

/** Overdue, due today and due in the next seven days, as three figures. */
export function TaskCounts({ tasks, today }: { tasks: readonly MyTaskDto[]; today: string }) {
  const figures = counts(tasks, today);
  const figure = (value: number, label: string, alert = false) => (
    <Box>
      <Typography
        component="p"
        sx={{
          fontSize: '1.75rem',
          fontWeight: 700,
          lineHeight: 1.1,
          color: alert && value > 0 ? 'error.main' : 'text.primary',
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
  return (
    <Stack direction="row" spacing={3} aria-label="Your tasks at a glance">
      {figure(figures.overdue, 'overdue', true)}
      {figure(figures.today, 'due today')}
      {figure(figures.week, 'in the next 7 days')}
      {figure(figures.open, 'open in all')}
    </Stack>
  );
}

/**
 * My tasks on the dashboard (CLR-66): the caller's open assigned tasks at a glance — overdue, due
 * today, due this week — and the first of each group with the time remaining.
 */
export function MyTasksCard({
  tasks,
  error,
  today,
}: {
  tasks: readonly MyTaskDto[] | undefined;
  error: Error | null;
  today: string;
}) {
  return (
    <Paper component="section" aria-label="My tasks" sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
      >
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}
        >
          <ListAltIcon color="primary" aria-hidden /> My tasks
        </Typography>
        <Button component={RouterLink} to="/my-tasks" size="small">
          All my tasks
        </Button>
      </Stack>
      {error !== null && (
        <Alert severity="error" variant="outlined">
          Your tasks could not be loaded: {errorMessage(error)}
        </Alert>
      )}
      {tasks !== undefined && tasks.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No open task is assigned to you.
        </Typography>
      )}
      {tasks !== undefined && tasks.length > 0 && (
        <Stack spacing={2}>
          <TaskCounts tasks={tasks} today={today} />
          <MyTaskList tasks={tasks} today={today} limit={3} />
        </Stack>
      )}
    </Paper>
  );
}
